import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import makeStyles from "@mui/styles/makeStyles";
import StarterKit from "@tiptap/starter-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faItalic,
  faLink,
  faList,
  faListOl,
  faUnderline,
  faRemoveFormat,
  faX,
  faText,
  faH1,
  faH2,
  faH3,
  faH4,
  faH5,
  faH6,
} from "@fortawesome/pro-regular-svg-icons";

import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { isBlank, notBlank } from "../../util/ext";

// Block-style options for the toolbar dropdown: Normal (paragraph) + Heading 1-6.
const BLOCK_OPTIONS = [
  { value: "paragraph", label: "Normal", icon: faText },
  { value: "1", label: "Heading 1", icon: faH1 },
  { value: "2", label: "Heading 2", icon: faH2 },
  { value: "3", label: "Heading 3", icon: faH3 },
  { value: "4", label: "Heading 4", icon: faH4 },
  { value: "5", label: "Heading 5", icon: faH5 },
  { value: "6", label: "Heading 6", icon: faH6 },
];

const useStyles = makeStyles(theme => ({
  close: {
    position: "absolute",
    fontSize: "1rem",
    right: 10,
    top: 5,
  },

  linkBtn: {
    fontSize: "inherit",
    color: "inherit",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  menu: {
    borderBottom: "1px dashed black",
    padding: theme.spacing(1),
  },

  menuBtn: {
    padding: theme.spacing(0, 1),
    cursor: "pointer",
    "&:hover": {
      color: "#06c",
    },
  },

  active: {
    color: "#06c",
  },

  group: {
    padding: theme.spacing(0, 1),
    display: "inline-block",
  },

  styleSelect: {
    verticalAlign: "middle",
    "& .MuiSelect-select": {
      padding: theme.spacing(0, 3, 0, 1),
      display: "flex",
      alignItems: "center",
      minHeight: "auto",
    },
    "&:hover": {
      color: "#06c",
    },
  },

  styleOptionIcon: {
    width: 16,
    marginRight: theme.spacing(1),
  },

  modal: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
  },

  paper: {
    position: "relative",
    width: 350,
    backgroundColor: "white",
    borderRadius: "20px",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },

  textBtn: {
    padding: 0,
  },

  content: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,

    "& .ProseMirror": {
      flexGrow: 1,
      minHeight: 150,
      outline: "none",
      cursor: "text",
      padding: theme.spacing(1, 0),
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",

      "& p": {
        margin: "0 0 8px",
      },
    },

    "& .ProseMirror:focus, & .ProseMirror-focused": {
      outline: "none",
    },
  },
}));

const LinkMenu = ({ editor, linkMenu, setLinkMenu }) => {
  const classes = useStyles();

  const [url, setUrl] = useState("");
  const [urlText, setUrlText] = useState("");

  const onUrlChange = e => {
    const url = e.target.value;
    setUrl(url);
  };
  const onUrlTextChange = e => {
    const urlText = e.target.value;
    setUrlText(urlText);
  };

  const handleLinkMenuClose = useCallback(() => {
    setLinkMenu(false);
    setUrl("");
    setUrlText("");
  }, [setLinkMenu]);

  useEffect(() => {
    if (linkMenu && editor) {
      const prevUrl = editor.getAttributes("link").href;
      const url = isBlank(prevUrl) ? "https://" : prevUrl;
      setUrl(url);

      const { view, state } = editor;
      const { from, to } = view.state.selection;
      const text = state.doc.textBetween(from, to, "");
      setUrlText(text);
    }
  }, [linkMenu, editor]);

  const onHandleLinkApply = useCallback(() => {
    if (url === null) {
      return;
    }

    const { from, to } = editor.view.state.selection;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }

    const text = isBlank(urlText) ? url : urlText;

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url, target: "_blank" })
      .insertContentAt({ from, to }, text)
      .run();

    handleLinkMenuClose();
  }, [url, urlText, editor, handleLinkMenuClose]);

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
    setLinkMenu(false);
  };
  return (
    <Fragment>
      <Modal open={linkMenu} className={classes.modal}>
        <div className={classes.paper}>
          <IconButton
            className={classes.close}
            onClick={handleLinkMenuClose}
            size="large"
          >
            <FontAwesomeIcon icon={faX} />
          </IconButton>
          <TextField
            fullWidth
            label="Text"
            value={urlText}
            onChange={onUrlTextChange}
            style={{ margin: "25px 0 7.5px 0" }}
          />
          <TextField
            fullWidth
            label="URL"
            value={url || ""}
            onChange={onUrlChange}
            style={{ margin: "7.5px 0 15px 0" }}
          />

          <div style={{ display: "flex" }}>
            <Button
              style={{ marginRight: 20 }}
              disabled={isBlank(url)}
              variant="contained"
              onClick={onHandleLinkApply}
            >
              Apply
            </Button>

            <Button
              disabled={isBlank(url)}
              className={classes.textBtn}
              onClick={removeLink}
            >
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

const MenuBar = ({ editor, selected, enableLinks = true }) => {
  const classes = useStyles();

  const [linkMenu, setLinkMenu] = useState(false);
  // const [linkMenu, setLinkMenu] = useState(null);

  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor.chain().focus().toggleUnderline().run();

  const toggleBulletList = () =>
    editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () =>
    editor.chain().focus().toggleOrderedList().run();
  const clearFormatting = () => editor.chain().focus().unsetAllMarks().run();

  const [disabled, setDisabled] = useState(selected);
  useEffect(() => {
    if (editor) {
      setDisabled(editor.state.selection.empty);
    }
  }, [editor, selected]);

  if (!editor) {
    return null;
  }

  const activeHeading = [1, 2, 3, 4, 5, 6].find(level =>
    editor.isActive("heading", { level }),
  );
  const currentBlock = activeHeading ? String(activeHeading) : "paragraph";

  const onBlockChange = event => {
    const value = event.target.value;
    const chain = editor.chain().focus();
    if (value === "paragraph") {
      chain.setParagraph().run();
    } else {
      chain.setHeading({ level: Number(value) }).run();
    }
  };

  return (
    <div className={classes.menu}>
      {enableLinks && (
        <LinkMenu
          editor={editor}
          linkMenu={linkMenu}
          setLinkMenu={setLinkMenu}
        />
      )}
      <div className={classes.group}>
        <Select
          value={currentBlock}
          onChange={onBlockChange}
          variant="standard"
          disableUnderline
          className={classes.styleSelect}
          renderValue={value => {
            const option =
              BLOCK_OPTIONS.find(o => o.value === value) || BLOCK_OPTIONS[0];
            return <FontAwesomeIcon icon={option.icon} title={option.label} />;
          }}
        >
          {BLOCK_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              <FontAwesomeIcon
                icon={option.icon}
                className={classes.styleOptionIcon}
              />
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.group}>
        <FontAwesomeIcon
          onClick={toggleBold}
          className={`${classes.menuBtn} ${
            editor.isActive("bold") ? classes.active : ""
          }`}
          icon={faBold}
        />
        <FontAwesomeIcon
          onClick={toggleItalic}
          className={`${classes.menuBtn} ${
            editor.isActive("italic") ? classes.active : ""
          }`}
          icon={faItalic}
        />
        <FontAwesomeIcon
          onClick={toggleUnderline}
          className={`${classes.menuBtn} ${
            editor.isActive("underline") ? classes.active : ""
          }`}
          icon={faUnderline}
        />
      </div>
      {enableLinks && (
        <div className={classes.group}>
          <IconButton
            disabled={disabled}
            disableRipple
            className={`${classes.menuBtn} ${classes.linkBtn}`}
            onClick={e => {
              e.preventDefault();
              setLinkMenu(true);
            }}
            size="large"
          >
            <FontAwesomeIcon
              className={
                editor.isActive("link") && !disabled ? classes.active : ""
              }
              icon={faLink}
            />
          </IconButton>
        </div>
      )}
      <div className={classes.group}>
        <FontAwesomeIcon
          onClick={toggleBulletList}
          className={`${classes.menuBtn} ${
            editor.isActive("bulletList") ? classes.active : ""
          }`}
          icon={faList}
        />
        <FontAwesomeIcon
          onClick={toggleOrderedList}
          className={`${classes.menuBtn} ${
            editor.isActive("orderedList") ? classes.active : ""
          }`}
          icon={faListOl}
        />
      </div>
      <div className={classes.group}>
        <FontAwesomeIcon
          onClick={clearFormatting}
          className={classes.menuBtn}
          icon={faRemoveFormat}
        />
      </div>
    </div>
  );
};

const RichTextEditor = ({
  editable,
  html,
  enableLinks = true,
  onUpdate,
  onBlur,
}) => {
  const classes = useStyles();
  const [selected, setSelected] = useState(false);

  const editor = useEditor({
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
      },
    },
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: enableLinks,
          linkOnPaste: enableLinks,
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      onUpdate?.({ text: editor.getText(), html: editor.getHTML() });
    },
    onBlur: () => onBlur?.(),
    onSelectionUpdate: ({ editor }) => {
      setSelected(!editor.state.selection.empty);
    },
  });

  useEffect(() => {
    if (notBlank(html) && editor) {
      const { from, to } = editor.state.selection;

      // emitUpdate: false = do not emit an update for programmatic content
      // changes (avoids the parent->editor->onUpdate->parent write-back loop);
      // preserveWhitespace keeps meaningful whitespace.
      // https://github.com/ueberdosis/tiptap/issues/2216
      editor.commands.setContent(html, {
        emitUpdate: false,
        parseOptions: { preserveWhitespace: "full" },
      });

      editor.commands.setTextSelection({ from, to });
    }
  }, [html, editor]);

  useEffect(() => {
    if (editor) {
      // `false` = suppress the update event setEditable emits by default;
      // toggling editability is not a content change and must not surface as
      // an onUpdate (it would write seed content back to parents on mount).
      editor.setEditable(Boolean(editable), false);
    }
  }, [editable, editor]);

  return (
    <Fragment>
      <MenuBar editor={editor} selected={selected} enableLinks={enableLinks} />
      <EditorContent editor={editor} className={classes.content} />
    </Fragment>
  );
};

export default RichTextEditor;
